import React from "react";
import { Loader2 } from "lucide-react";

const PortfolioPnlPanel = ({ data, loading, error }) => {
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);
  const formatPercent = (value) => {
    const fixed = Number(value || 0).toFixed(2);
    return `${fixed.replace(/\.?0+$/, "")}%`;
  };
  const marginValue = Math.min(Math.max(Number(data?.netMargin || 0), 0), 100);

  if (loading && (!data || Object.keys(data).length === 0)) {
    return (
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 h-full animate-pulse">
        <div className="flex justify-between mb-6">
          <div>
            <div className="h-6 w-32 dark:bg-slate-800 bg-slate-200 rounded mb-2"></div>
            <div className="h-3 w-48 dark:bg-slate-800 bg-slate-200 rounded"></div>
          </div>
          <div className="h-6 w-20 dark:bg-slate-800 bg-slate-200 rounded-full"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800/50 border-slate-200 rounded-xl p-4 h-24">
              <div className="h-3 w-24 dark:bg-slate-800 bg-slate-200 rounded mb-3"></div>
              <div className="h-8 w-32 dark:bg-slate-800 bg-slate-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 h-full animate-slide-up delay-100" style={{ animationFillMode: 'both' }}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h3 className="dark:text-white text-slate-900 font-bold text-lg">Portfolio P&L</h3>
          <p className="text-xs text-slate-500 mt-1">
            {data?.caseCount ? `Across ${data.caseCount} active cases` : "No P&L data yet"}
          </p>
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-400 dark:bg-slate-800/50 bg-slate-100 px-2 py-1 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin text-yellow-400" />
            Updating...
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800/50 border-slate-200 rounded-xl p-4 dark:hover:bg-slate-900 hover:bg-white dark:hover:border-slate-700 hover:border-slate-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium group-hover:text-emerald-400/80 transition-colors">Total Revenue</p>
            <p className="text-2xl font-bold text-emerald-400 mt-2 tracking-tight group-hover:text-emerald-300">
              {formatCurrency(data?.totalRevenue)}
            </p>
          </div>
          <div className="dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800/50 border-slate-200 rounded-xl p-4 dark:hover:bg-slate-900 hover:bg-white dark:hover:border-slate-700 hover:border-slate-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium group-hover:text-red-400/80 transition-colors">Total Expenses</p>
            <p className="text-2xl font-bold text-red-400 mt-2 tracking-tight group-hover:text-red-300">
              {formatCurrency(data?.totalExpenses)}
            </p>
          </div>
          <div className="dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800/50 border-slate-200 rounded-xl p-4 dark:hover:bg-slate-900 hover:bg-white dark:hover:border-slate-700 hover:border-slate-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium dark:group-hover:text-white group-hover:text-slate-900 transition-colors">Net Income</p>
            <p className="text-2xl font-bold dark:text-white text-slate-900 mt-2 tracking-tight">
              {formatCurrency(data?.net)}
            </p>
          </div>
          <div className="dark:bg-slate-950/50 bg-slate-50 border dark:border-slate-800/50 border-slate-200 rounded-xl p-4 dark:hover:bg-slate-900 hover:bg-white dark:hover:border-slate-700 hover:border-slate-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group">
            <div className="flex justify-between items-baseline">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-medium group-hover:text-yellow-400/80 transition-colors">Net Margin</p>
              <p className="text-2xl font-bold text-yellow-400 tracking-tight group-hover:text-yellow-300">
                {formatPercent(data?.netMargin)}
              </p>
            </div>
            <div className="mt-3 h-1.5 dark:bg-slate-800 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-yellow-500 to-emerald-400 shadow-[0_0_8px_rgba(14,165,233,0.4)]"
                style={{ width: `${marginValue}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {!error && (
        <div className="mt-6 pt-4 border-t dark:border-slate-800/50 border-slate-200 flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded-lg dark:bg-slate-800/30 bg-slate-100 border dark:border-slate-700/50 border-slate-200 text-xs font-medium text-slate-400 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
            Avg Revenue: <span className="dark:text-slate-300 text-slate-600">{formatCurrency(data?.avgRevenuePerCase)}</span>
          </div>
          <div className="px-3 py-1.5 rounded-lg dark:bg-slate-800/30 bg-slate-100 border dark:border-slate-700/50 border-slate-200 text-xs font-medium text-slate-400 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
            Avg Net: <span className="dark:text-slate-300 text-slate-600">{formatCurrency(data?.avgNetPerCase)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPnlPanel;
